import React from 'react';
import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';

import { cn } from 'src/component/ds/utils';
import { Field, FieldDescription, FieldError, FieldLabel } from 'src/component/ds/ui/field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/component/ds/ui/select';
import RequiredLabel from './RequiredLabel';

interface FormSelectFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  label: string;
  options: { value: string | number; label: string }[];
  placeholder?: string;
  disabled?: boolean;
  description?: string;
  className?: string;
}

export default function FormSelectField<T extends FieldValues> ({
  control,
  name,
  label,
  options,
  placeholder,
  disabled,
  description,
  className
}: FormSelectFieldProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid} className={cn('ds-gap-1', className)}>
          <FieldLabel htmlFor={field.name} weight='normal'><RequiredLabel>{label}</RequiredLabel></FieldLabel>
          <Select
            name={field.name}
            value={field.value as string}
            onValueChange={field.onChange}
            disabled={disabled}
            onOpenChange={(open) => {
              if (!open) field.onBlur();
            }}
          >
            <SelectTrigger id={field.name} ref={field.ref} aria-invalid={fieldState.invalid}>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={String(option.value)}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
          {!fieldState.invalid && description && <FieldDescription>{description}</FieldDescription>}
        </Field>
      )}
    />
  );
}
